/**
 * [외부 이미지 rehost]
 * 역할: 에디터 본문 외부 img를 import API로 서버 저장 URL로 교체 (제출 시점에 호출)
 * 작성/수정일: 2026-07-29
 */
import { importImage } from './api.js';
import { EDITOR_IMAGE_MAX_WIDTH } from './image-upload.js';
import { getBoardImageMaxWidth } from './utils.js';

const HOSTED_EDITOR_IMAGE_PATH = /\/data\/editor\//;
const PASTE_EMOJI_IMAGE_SRC =
  /(?:gstatic\.com\/(?:youtube\/)?img\/emojis|twemoji|emoji(?:cdn)?|\/emoji\/)/i;
const EMOJI_ONLY_ALT = /^[\p{Extended_Pictographic}\uFE0F\u200D\s]+$/u;

/**
 * [외부 이미지 rehost]
 * 역할: 이미 우리 서버(/data/editor/)에 저장된 이미지인지 판별
 * 작성/수정일: 2026-07-29
 */
export function isHostedEditorImageUrl(src) {
  if (!src) return false;
  try {
    const url = new URL(src, window.location.origin);
    return HOSTED_EDITOR_IMAGE_PATH.test(url.pathname);
  } catch {
    return HOSTED_EDITOR_IMAGE_PATH.test(src);
  }
}

/**
 * [외부 이미지 rehost]
 * 역할: import API로 가져올 외부 HTTP(S) 이미지 URL인지 판별
 * 작성/수정일: 2026-07-29
 */
export function isExternalImportableImageUrl(src) {
  if (!src || src.startsWith('data:') || src.startsWith('blob:') || isHostedEditorImageUrl(src)) {
    return false;
  }
  try {
    const url = new URL(src);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * [외부 이미지 rehost]
 * 역할: YouTube gstatic 등 붙여넣기 이모지 img 제외
 * 작성/수정일: 2026-07-29
 */
export function isPasteEmojiImage(src, alt = '') {
  const trimmedSrc = String(src || '').trim();
  const trimmedAlt = String(alt || '').trim();
  if (trimmedSrc && PASTE_EMOJI_IMAGE_SRC.test(trimmedSrc)) return true;
  return Boolean(trimmedAlt) && EMOJI_ONLY_ALT.test(trimmedAlt);
}

/**
 * [외부 이미지 rehost]
 * 역할: 붙여넣기 HTML의 이모지 img를 alt 문자로 치환
 * 작성/수정일: 2026-07-29
 */
export function normalizePastedHtmlEmojis(html) {
  if (!html || typeof DOMParser === 'undefined') return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  let changed = false;
  doc.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src')?.trim() ?? '';
    const alt = img.getAttribute('alt')?.trim() ?? '';
    if (!isPasteEmojiImage(src, alt)) return;
    if (alt) img.replaceWith(doc.createTextNode(alt));
    else img.remove();
    changed = true;
  });
  return changed ? doc.body.innerHTML : html;
}

/**
 * [외부 이미지 rehost]
 * 역할: 붙여넣기 HTML에 재업로드 대상 외부 이미지가 있는지 검사
 * 작성/수정일: 2026-07-29
 */
export function htmlContainsExternalImages(html) {
  if (!html) return false;
  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(doc.querySelectorAll('img[src]')).some((img) => {
      const src = img.getAttribute('src')?.trim() ?? '';
      const alt = img.getAttribute('alt')?.trim() ?? '';
      return isExternalImportableImageUrl(src) && !isPasteEmojiImage(src, alt);
    });
  }
  return /<img\b[^>]*\bsrc=["']https?:\/\//i.test(html);
}

/**
 * [외부 이미지 rehost]
 * 역할: 에디터 문서에 import 대상 외부 이미지가 있는지 검사 (이미 /data/editor/ 는 제외)
 * 작성/수정일: 2026-07-29
 */
export function editorHasExternalImages(editor) {
  if (!editor?.state?.doc) return false;
  let found = false;
  editor.state.doc.descendants((node) => {
    if (found || node.type.name !== 'image') return;
    const src = String(node.attrs.src ?? '');
    const alt = String(node.attrs.alt ?? '');
    if (isExternalImportableImageUrl(src) && !isPasteEmojiImage(src, alt)) {
      found = true;
    }
  });
  return found;
}

/**
 * [외부 이미지 rehost]
 * 역할: 에디터 문서의 외부 img src를 import API로 재업로드 후 교체 (/data/editor/ 스킵)
 * 작성/수정일: 2026-07-29
 */
export async function rehostExternalImagesInEditor(editor, config) {
  const externalSources = new Set();

  editor.state.doc.descendants((node) => {
    if (node.type.name !== 'image') return;
    const src = String(node.attrs.src ?? '');
    const alt = String(node.attrs.alt ?? '');
    if (isExternalImportableImageUrl(src) && !isPasteEmojiImage(src, alt)) {
      externalSources.add(src);
    }
  });

  if (externalSources.size === 0) {
    return { replaced: 0, failed: 0 };
  }

  const imported = new Map();
  let failed = 0;

  await Promise.all(
    Array.from(externalSources).map(async (src) => {
      try {
        imported.set(src, await importImage(src, config));
      } catch {
        failed += 1;
      }
    }),
  );

  if (imported.size === 0) {
    return { replaced: 0, failed };
  }

  const updates = [];
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name !== 'image') return;
    const src = String(node.attrs.src ?? '');
    const uploaded = imported.get(src);
    if (!uploaded) return;

    // [이미지 폭] rehost 삽입 시에도 게시판 이미지 폭 상한 — 작성/수정일: 2026-08-09
    const maxW = getBoardImageMaxWidth(EDITOR_IMAGE_MAX_WIDTH) || EDITOR_IMAGE_MAX_WIDTH;
    const uploadedW = Number(uploaded.width) || maxW;
    const uploadedH = Number(uploaded.height) || 1;
    const currentWidth = Number(node.attrs.width ?? 0);
    const initialWidth = currentWidth > 0 ? Math.min(currentWidth, maxW) : Math.min(uploadedW, maxW);
    const initialHeight =
      currentWidth > 0
        ? Number(node.attrs.height ?? 0)
        : Math.max(1, Math.round(uploadedH * (initialWidth / uploadedW)));

    const alignment =
      node.attrs.alignment === 'left' || node.attrs.alignment === 'right' || node.attrs.alignment === 'center'
        ? node.attrs.alignment
        : 'center';

    updates.push({
      pos,
      attrs: {
        ...node.attrs,
        src: uploaded.url,
        alt: String(node.attrs.alt || uploaded.alt || ''),
        alignment,
        width: initialWidth,
        height: initialHeight > 0 ? initialHeight : undefined,
        uploading: false,
      },
    });
  });

  updates.sort((a, b) => b.pos - a.pos);
  const transaction = editor.state.tr;
  for (const update of updates) {
    transaction.setNodeMarkup(update.pos, undefined, update.attrs);
  }
  editor.view.dispatch(transaction);

  return { replaced: updates.length, failed };
}
